import { useTranslation } from 'react-i18next';
import { Reveal } from '../motion/Reveal';
import { LANGUAGES } from '../i18n';
import { BOT_APP_URL, SUPPORT_URL } from '../links';
import cardBg from '../assets/figma/images/footer-card.webp';
import logoWhite from '../assets/figma/logo-horizontal-white.svg';
import wordmark from '../assets/figma/wordmark-large.svg';
import mark from '../assets/figma/icons/footer-mark.svg';
import arrowUpBlue from '../assets/figma/icons/arrow-up-blue.svg';
import arrowDownWhite from '../assets/figma/icons/arrow-down-white.svg';
import telegram from '../assets/figma/icons/telegram.svg';

/** Figma 59:11477 / 59:11436 / 59:11517 / 59:9941 — a blue card and a white one
 *  side by side, the app mark overlapping their top-right corner, and the
 *  wordmark running the full width beneath. */
export function Footer() {
  const { t, i18n } = useTranslation();
  const colApp = t('footer.colApp', { returnObjects: true }) as string[];
  const colSite = t('footer.colSite', { returnObjects: true }) as string[];
  const current = LANGUAGES.find((l) => l.code === i18n.language) ?? LANGUAGES[0];

  return (
    <footer className="mx-auto w-full px-[16px] lg:w-[1208px] lg:px-0">
      <Reveal className="relative flex flex-col gap-[16px] lg:flex-row">
        {/* blue card */}
        <div className="relative h-[300px] w-full overflow-hidden rounded-[32px] lg:h-[388px] lg:w-[392px] lg:rounded-[44px]">
          <img src={cardBg} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <img
            src={logoWhite}
            alt="AsiaWallet"
            className="absolute left-[24px] top-[24px] h-[32px] w-[160px] lg:left-[32px] lg:top-[32px] lg:h-[41px] lg:w-[205px]"
          />
          <p className="absolute bottom-[86px] left-[24px] whitespace-nowrap text-[20px] font-medium leading-[0.9] text-white lg:bottom-auto lg:left-[32px] lg:top-[277px] lg:text-[24px]">
            {t('footer.tagline')}
          </p>
          <a
            href={BOT_APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-[24px] left-[24px] flex items-center justify-center gap-[3px] rounded-[999px] bg-white px-[16px] py-[10px] text-[15px] font-medium text-primary lg:bottom-auto lg:left-[32px] lg:top-[326px]"
          >
            {t('footer.getCard')}
            <img src={arrowUpBlue} alt="" className="size-[16px]" />
          </a>
          <button
            type="button"
            onClick={() => void i18n.changeLanguage(current.code === 'ru' ? 'en' : 'ru')}
            className="absolute bottom-[24px] left-[180px] flex items-center justify-center gap-[3px] px-[16px] py-[10px] text-[15px] font-medium text-white lg:bottom-auto lg:left-[188px] lg:top-[326px]"
          >
            {current.label}
            <img src={arrowDownWhite} alt="" className="size-[16px]" />
          </button>
        </div>

        {/* white card */}
        <div className="flex w-full flex-col rounded-[32px] bg-white p-[24px] lg:h-[388px] lg:w-[800px] lg:rounded-[44px] lg:p-[32px]">
          <div className="flex w-full flex-wrap items-start gap-x-[32px] gap-y-[24px] lg:w-[512px] lg:flex-nowrap lg:justify-between lg:gap-0">
            <nav className="flex flex-col justify-center gap-[12px] lg:gap-[20px]">
              {[
                { label: colApp[0], href: BOT_APP_URL, external: true },
                { label: colApp[1], href: '#features' },
                { label: colApp[2], href: '#coverage' },
                { label: colApp[3], href: '#trust' },
                { label: colApp[4], href: '#faq' },
              ].map(({ label, href, external }) =>
                label ? (
                  <a
                    key={label}
                    href={href}
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="whitespace-nowrap text-[15px] font-medium text-ink"
                  >
                    {label}
                  </a>
                ) : null,
              )}
            </nav>
            <nav className="flex flex-col justify-center gap-[12px] lg:gap-[20px]">
              {[
                { label: colSite[0], href: '#' },
                { label: colSite[1], href: BOT_APP_URL, external: true },
                { label: colSite[2], href: SUPPORT_URL, external: true },
              ].map(({ label, href, external }) =>
                label ? (
                  <a
                    key={label}
                    href={href}
                    {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                    className="whitespace-nowrap text-[15px] font-medium text-ink"
                  >
                    {label}
                  </a>
                ) : null,
              )}
            </nav>
            <div className="flex flex-col justify-center gap-[12px]">
              <p className="whitespace-nowrap text-[15px] font-medium text-ink">{t('footer.social')}</p>
              <div className="flex gap-[8px]">
                <a
                  href={BOT_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Telegram"
                  className="flex size-[40px] items-center justify-center rounded-[12px] bg-ink"
                >
                  <img src={telegram} alt="" className="size-[22px]" />
                </a>
                <a
                  href={SUPPORT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Support"
                  className="flex size-[40px] items-center justify-center rounded-[12px] bg-ink"
                >
                  <img src={telegram} alt="" className="size-[22px]" />
                </a>
              </div>
            </div>
          </div>

          <div className="mt-[24px] flex w-full flex-col items-start gap-[8px] text-[13px] font-semibold leading-[1.3] tracking-[-0.5px] text-black/80 lg:mt-auto lg:flex-row lg:items-center lg:justify-between lg:gap-0 lg:whitespace-nowrap lg:text-[15px]">
            <p>{t('footer.copyright')}</p>
            <div className="flex items-center gap-[16px] lg:gap-[24px] lg:text-right">
              <a href="#terms" className="underline">
                {t('footer.terms')}
              </a>
              <a href="#privacy" className="underline">
                {t('footer.privacy')}
              </a>
            </div>
          </div>
        </div>

        {/* app mark, overlapping both cards' top edge */}
        <img
          src={mark}
          alt=""
          className="aw-drift-fixed pointer-events-none absolute right-[14px] top-[14px] size-[84px] max-w-none lg:left-[966.85px] lg:right-auto lg:top-[-58.45px] lg:size-[187.7px]"
          style={{ '--aw-angle': '-21.8deg' } as React.CSSProperties}
        />
      </Reveal>

      <Reveal delay={0.15}>
        <img src={wordmark} alt="AsiaWallet" className="mt-[16px] h-auto w-full lg:mt-[21px] lg:h-[197px] lg:w-[1208px]" />
      </Reveal>
    </footer>
  );
}
